import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { H1Title } from "../../components/typography/typography";
import api from "../../services/api";
import { Error as ToastError } from "../../components/toast/toast";

const STATUS_STYLE = {
  forming: 'bg-orange-500/10 text-orange-dark',
  complete: 'bg-green/10 text-green',
  disqualified: 'bg-red-500/10 text-red-500',
};

const FILTERS = ['all', 'forming', 'complete', 'disqualified'];

const acceptedCount = (team) =>
  (team.members || []).filter(m => m.status === 'accepted').length;

export default function AdminTeams() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [pagination, setPagination] = useState({ total: 0 });

  useEffect(() => { loadTeams(); }, [status]);

  const loadTeams = async () => {
    setLoading(true);
    try {
      const params = status === 'all' ? {} : { status };
      const { data } = await api.get('/admin/teams', { params });
      setTeams(data.data.teams || []);
      setPagination({ total: data.data.total });
    } catch (err) {
      ToastError('Failed to load teams');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <H1Title size="sm">Teams</H1Title>

      {/* Status Filter */}
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f}
            onClick={() => setStatus(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
              status === f ? 'bg-primary text-white' : 'bg-grey-bg text-grey-667 hover:bg-grey-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <span className="self-center text-sm text-grey-98 ml-2">{pagination.total} total</span>
      </div>

      {/* Teams Table */}
      <div className="bg-white rounded-xl border border-grey-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-grey-667">Loading...</div>
        ) : teams.length === 0 ? (
          <div className="p-12 text-center text-grey-667">No teams found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-grey-bg border-b border-grey-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Team</th>
                <th className="text-left px-4 py-3 font-semibold">Leader</th>
                <th className="text-left px-4 py-3 font-semibold">Members</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Created</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-200">
              {teams.map(t => (
                <tr
                  key={t._id}
                  className="hover:bg-grey-bg/50 cursor-pointer"
                  onClick={() => navigate(`/admin/teams/${t._id}`)}
                >
                  <td className="px-4 py-3 font-semibold">{t.name}</td>
                  <td className="px-4 py-3 text-grey-667">
                    {t.leaderId ? `${t.leaderId.firstName} ${t.leaderId.lastName}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-grey-667">{acceptedCount(t)} / 3</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLE[t.status] || 'bg-grey-bg text-grey-667'}`}>{t.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-grey-98">{new Date(t.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/teams/${t._id}`); }}
                      className="text-primary hover:underline text-sm font-medium cursor-pointer"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
